import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, Snackbar, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { getUserRights, updateUserRight, getUsersWithOverrides } from '../../api/roleRightApi';
import { getAllAgents } from '../../api/userApi';
import handleAgentName from '../../utils/userUtils';

// Per-user overrides. The role sets the baseline; a row here beats it in either
// direction, which is how one agent gets CSC back without opening it for the whole
// Sales Agent role, and how one agent loses it without touching anyone else.
// Three states, matching what the API stores:
//   Follow role  -> no userright row  (Remove: true)
//   Allow        -> IsGranted = 1
//   Block        -> IsGranted = 0
// (user 2026-09-27)
const STATES = [
  { key: 'role', label: 'Use default', colour: '#64748B', bg: '#F1F5F9' },
  { key: 'allow', label: 'Allow', colour: '#15803D', bg: '#DCFCE7' },
  { key: 'block', label: 'Block', colour: '#B91C1C', bg: '#FEE2E2' },
];

export default function UserPermissionPanel({ sections, FONT, sensitiveSections = [] }) {
  const [agents, setAgents] = useState([]);
  const [picked, setPicked] = useState(null);
  const [rows, setRows] = useState([]);          // UserRightResponse[]
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  // Everyone who carries a per-user row. Without this the only way to find an exception
  // was to open each agent in turn. (user 2026-09-27)
  const [overrides, setOverrides] = useState([]);
  // Commission and Accounting are read-only until deliberately unlocked. It matters
  // more here than in By Role: there is no Save button on this tab - a click lands
  // straight on the database - so a mis-tap changes someone's access there and then.
  // The unlock clears when you pick a different person. (user 2026-09-27)
  const [unlocked, setUnlocked] = useState(false);

  const loadOverrides = useCallback(() => {
    getUsersWithOverrides().then(r => setOverrides(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  useEffect(() => {
    getAllAgents()
      .then(r => setAgents(r.data || []))
      .catch(() => setSnack({ open: true, message: 'Could not load the user list', severity: 'error' }));
  }, []);

  const load = useCallback(async (userId) => {
    if (!userId) { setRows([]); return; }
    setBusy(true);
    try {
      const r = await getUserRights(userId);
      setRows(r.data || []);
    } catch {
      setSnack({ open: true, message: 'Could not load this user’s permissions', severity: 'error' });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(picked?.userId); setUnlocked(false); }, [picked, load]);

  const byName = useMemo(() => {
    const m = {};
    rows.forEach(r => { m[r.rightName] = r; });
    return m;
  }, [rows]);

  const stateOf = (rightName) => {
    const r = byName[rightName];
    if (!r || r.userOverride === null || r.userOverride === undefined) return 'role';
    return r.userOverride ? 'allow' : 'block';
  };

  const setState = async (right, next) => {
    if (!picked?.userId || !right) return;
    setBusy(true);
    try {
      await updateUserRight({
        userId: picked.userId,
        rightId: right.rightId,
        isGranted: next === 'allow',
        remove: next === 'role',
      });
      await load(picked.userId);
      loadOverrides();
      setSnack({ open: true, message: 'Saved', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Save failed', severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const label = (a) => `${handleAgentName(a.firstName, a.lastName, a.alias)}${a.username ? ` (${a.username})` : ''}`;
  const ovFor = (userId) => overrides.find(o => o.userId === userId);
  const isLocked = (sectionName) => !unlocked && sensitiveSections.includes(sectionName);

  return (
    <Box>
      <Box sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', p: 2, mb: 1.5 }}>
        <Autocomplete
          options={agents}
          value={picked}
          onChange={(_, v) => setPicked(v)}
          getOptionLabel={label}
          isOptionEqualToValue={(a, b) => a.userId === b.userId}
          renderOption={(props, a) => {
            const o = ovFor(a.userId);
            return (
              <li {...props} key={a.userId}>
                <span style={{ flex: 1 }}>{label(a)}</span>
                {o && (
                  <Chip size="small" label={`${o.blocked.length ? `−${o.blocked.length} ` : ''}${o.allowed.length ? `+${o.allowed.length}` : ''}`.trim()}
                    sx={{ ...FONT, height: 18, fontSize: '0.6rem', ml: 1,
                      backgroundColor: o.blocked.length ? '#FEE2E2' : '#DCFCE7',
                      color: o.blocked.length ? '#B91C1C' : '#15803D' }} />
                )}
              </li>
            );
          }}
          size="small"
          sx={{ maxWidth: 460 }}
          renderInput={(params) => <TextField {...params} label="User" placeholder="Search by name or username" />}
        />
        {picked && (
          <Typography sx={{ ...FONT, fontSize: '0.75rem', color: '#64748B', mt: 1 }}>
            Roles: {((picked.userRoles || []).map(r => r.roleName).filter(Boolean).join(', ')) || '—'}
            {picked.createdOn ? ` · registered ${String(picked.createdOn).slice(0, 10)}` : ''}
            {ovFor(picked.userId)
              ? ` · ${ovFor(picked.userId).blocked.length} blocked, ${ovFor(picked.userId).allowed.length} allowed`
              : ' · no overrides — follows the role throughout'}
          </Typography>
        )}
      </Box>

      {!picked && (
        <Box sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', p: 3 }}>
          <Typography sx={{ ...FONT, fontSize: '0.82rem', color: '#64748B' }}>
            Pick a user to see what their role gives them, and to allow or block anything for them alone.
          </Typography>
        </Box>
      )}

      {picked && sensitiveSections.length > 0 && (
        <Box sx={{ mb: 1.5, p: 1.25, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
          backgroundColor: unlocked ? '#FEF2F2' : '#FFFBEB', border: `1px solid ${unlocked ? '#FECACA' : '#FDE68A'}` }}>
          <Typography sx={{ ...FONT, fontSize: '0.76rem', color: unlocked ? '#991B1B' : '#92400E' }}>
            {unlocked
              ? 'Commission and Accounting are unlocked. There is no Save on this tab \u2014 a click applies immediately.'
              : 'Commission and Accounting are read-only until you unlock. Everything else can be changed straight away.'}
          </Typography>
          <Button
            onClick={() => setUnlocked(u => !u)}
            variant={unlocked ? 'outlined' : 'contained'}
            size="small"
            sx={{ ...FONT, textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', whiteSpace: 'nowrap',
              ...(unlocked ? { borderColor: '#FCA5A5', color: '#991B1B' } : { backgroundColor: '#D97706', '&:hover': { backgroundColor: '#B45309' } }) }}
          >
            {unlocked ? 'Lock again' : 'Unlock to edit'}
          </Button>
        </Box>
      )}

      {picked && (
        <Typography sx={{ ...FONT, fontSize: '0.72rem', color: '#64748B', mb: 1 }}>
          <b>Default</b> is what this person&apos;s role gives everyone on it.
          An <b>override</b> changes it for this person alone.
          <b> Result</b> is what they actually get — the override when there is one, the default otherwise.
        </Typography>
      )}

      {picked && (
        <TableContainer sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                <TableCell sx={{ ...FONT, fontWeight: 700, fontSize: '0.75rem', color: '#475569' }}>Feature</TableCell>
                <TableCell sx={{ ...FONT, fontWeight: 700, fontSize: '0.75rem', color: '#475569', width: 130 }}>Default (role)</TableCell>
                <TableCell sx={{ ...FONT, fontWeight: 700, fontSize: '0.75rem', color: '#475569', width: 110 }}>Result</TableCell>
                <TableCell sx={{ ...FONT, fontWeight: 700, fontSize: '0.75rem', color: '#475569', width: 280 }}>Override for this user</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sections.map(sec => {
                // A right can be listed twice across sections (WebbeyCommission is), so
                // only the first listing gets a row - two rows for one right would let the
                // reader set it in one place and see it unchanged in the other.
                const seen = new Set();
                const feats = sec.features.filter(f => (seen.has(f.rightName) ? false : seen.add(f.rightName)));
                return [
                  <TableRow key={`s-${sec.section}`}>
                    <TableCell colSpan={4} sx={{ ...FONT, fontWeight: 700, fontSize: '0.72rem', color: '#0F172A', backgroundColor: '#F1F5F9', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {sec.section}
                      {sensitiveSections.includes(sec.section) && (
                        <span style={{ marginLeft: 10, fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.4,
                          color: isLocked(sec.section) ? '#92400E' : '#B91C1C' }}>
                          {isLocked(sec.section) ? 'LOCKED' : 'UNLOCKED'}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>,
                  ...feats.map(f => {
                    const r = byName[f.rightName];
                    const st = stateOf(f.rightName);
                    return (
                      <TableRow key={`${sec.section}-${f.rightName}`} hover
                        sx={{ ...(st !== 'role' ? { backgroundColor: '#FFFBEB' } : {}),
                              ...(isLocked(sec.section) ? { opacity: 0.55 } : {}) }}>
                        <TableCell sx={{ ...FONT, fontSize: '0.78rem', color: '#0F172A' }}>
                          {f.label}
                          {!r && <Chip size="small" label="not in DB yet" sx={{ ...FONT, ml: 1, height: 18, fontSize: '0.6rem', backgroundColor: '#FEF3C7', color: '#92400E' }} />}
                        </TableCell>
                        <TableCell sx={{ ...FONT, fontSize: '0.75rem', color: r?.fromRole ? '#15803D' : '#94A3B8' }}>
                          {r?.fromRole ? 'Allowed' : 'Not allowed'}
                        </TableCell>
                        <TableCell sx={{ ...FONT, fontSize: '0.75rem', fontWeight: 700, color: r?.effectiveAccess ? '#15803D' : '#B91C1C' }}>
                          {r ? (r.effectiveAccess ? 'Can access' : 'No access') : '—'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {STATES.map(s => (
                              <Button
                                key={s.key}
                                size="small"
                                disabled={!r || busy || isLocked(sec.section)}
                                onClick={() => setState(r, s.key)}
                                sx={{
                                  ...FONT, textTransform: 'none', fontSize: '0.68rem', fontWeight: 600,
                                  minWidth: 0, px: 1, py: 0.2, borderRadius: '6px',
                                  color: st === s.key ? '#fff' : s.colour,
                                  backgroundColor: st === s.key ? s.colour : s.bg,
                                  '&:hover': { backgroundColor: st === s.key ? s.colour : s.bg },
                                }}
                              >
                                {s.label}
                              </Button>
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }),
                ];
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar open={snack.open} autoHideDuration={2000} onClose={() => setSnack(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} variant="filled" sx={{ ...FONT, fontSize: '0.8rem' }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
